import { Atom } from "effect/unstable/reactivity"

/**
 * The three colour schemes the token stylesheets project. `light` and `dark`
 * are the two halves of the user-facing toggle; `hc` is an accessibility
 * overlay (ADR 0004) with white surfaces, black ink and a >=7:1 accent, and it
 * is deliberately NOT part of that toggle — it engages from the OS
 * `prefers-contrast: more` preference, or from an explicit `setTheme("hc")`.
 */
export type Theme = "light" | "dark" | "hc"

const STORAGE_KEY = "ffn.theme"

/**
 * The key earlier builds wrote, read once as a fallback and never written.
 *
 * A storage key is state living in a browser this repo does not control: a reader who
 * chose dark six months ago has that choice under the old key, and a rename with no
 * fallback silently reverts them to the OS preference on their next visit. That reads
 * as a bug in the toggle rather than as a rename, which is why the migration is worth
 * four lines. Remove it when reverting a stale preference costs nothing.
 */
const STORAGE_KEY_LEGACY = "FFN.theme"

const isTheme = (value: unknown): value is Theme =>
  value === "light" || value === "dark" || value === "hc"

/**
 * Resolve the scheme to start in. The order encodes who gets to decide: an
 * explicit stored choice always wins, then the OS accessibility preference
 * (contrast outranks scheme — a user who asked for more contrast gets `hc`
 * rather than `dark`), then the OS scheme, then light.
 *
 * SSR-safe: returns "light" with no `window`. The stored read is wrapped
 * because `localStorage` throws outright in some private-browsing modes, and a
 * theme preference is not worth failing a boot over.
 */
export const initialTheme = (): Theme => {
  if (typeof window === "undefined") return "light"
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY)
    if (isTheme(saved)) return saved
    const legacy = window.localStorage.getItem(STORAGE_KEY_LEGACY)
    if (isTheme(legacy)) return legacy
  } catch {
    // Storage unavailable (private mode, disabled) — fall through to OS prefs.
  }
  if (window.matchMedia?.("(prefers-contrast: more)").matches) return "hc"
  if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) return "dark"
  return "light"
}

/**
 * The theme is client-owned cross-tree state (ownership table row: Effect
 * Atom), seeded from the stored preference falling back to the OS preferences.
 * `applyTheme` is the single DOM writer: html[data-theme] is what the token
 * stylesheets key on.
 */
export const themeAtom = Atom.make<Theme>(initialTheme())

export const applyTheme = (theme: Theme): void => {
  document.documentElement.dataset.theme = theme
  window.localStorage.setItem(STORAGE_KEY, theme)
}

/**
 * The other half of the light/dark toggle. `hc` is excluded from the cycle, so
 * a toggle pressed while high-contrast is engaged lands on `dark` (the same
 * branch `light` takes) and hands the scheme back to the user rather than
 * bouncing between an accessibility overlay and a colour scheme. Reach `hc`
 * through the OS preference or an explicit set, never through this.
 */
export const oppositeTheme = (theme: Theme): Theme => (theme === "dark" ? "light" : "dark")
